import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MENU, MENU_EXTRAS } from "../data/site";
import { INGREDIENTS_EN, NAME_EN } from "../data/menu_en";
import { Reveal, SectionLabel } from "../components/Reveal";
import { useLang } from "../i18n";

const CAT_EN = {
  classiche: "The Classics",
  speciali: "The Specials",
  calzoni: "Calzoni",
  dolci: "Dessert Pizzas",
  contorni: "Sides & To Finish",
  bevande: "Drinks & Beers",
  vini: "Wines",
};

const EXTRA_EN = {
  "Per Finire": "To Finish",
  "Birre Angelo Poretti": "Angelo Poretti Beers",
  "Birra Artigianale OBS": "OBS Craft Beer",
  "Vino Bianco": "White Wine",
  "Vino Rosato": "Rosé Wine",
  "Bollicine": "Sparkling",
  "Vino della Casa": "House Wine",
};

const EXTRAS_EN = {
  Coperto: "Cover charge",
  Aggiunte: "Extra toppings",
  "Impasto Farina di Timilia": "Timilia flour dough",
  "Impasto Farina Integrale": "Whole wheat dough",
};

const MenuRow = ({ item, lang }) => {
  const name = lang === "en" ? NAME_EN[item.name] || item.name : item.name;
  const ingredients = lang === "en" ? INGREDIENTS_EN[item.name] ?? item.ingredients : item.ingredients;
  return (
    <div className="group" data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-baseline gap-3">
        <h4 className="font-head font-bold uppercase tracking-wide text-lg text-foreground group-hover:text-neon transition-colors duration-300">
          {name}
        </h4>
        <span className="flex-1 border-b border-dotted border-foreground/25 -translate-y-1 group-hover:border-neon/40 transition-colors duration-300" />
        <span className="font-display text-xl text-neon">€{item.price}</span>
      </div>
      {ingredients && (
        <p className="text-sm text-fog mt-1.5 leading-relaxed pr-10">{ingredients}</p>
      )}
    </div>
  );
};

const MenuSection = ({ title, items, lang }) => (
  <div>
    <h3 className="font-head font-bold uppercase tracking-[0.25em] text-sm text-neon mb-6 flex items-center gap-4">
      {title}
      <span className="h-px flex-1 bg-neon/20" />
    </h3>
    <div className="space-y-6">
      {items.map((item) => (
        <MenuRow key={item.name} item={item} lang={lang} />
      ))}
    </div>
  </div>
);

export default function MenuPage() {
  const [active, setActive] = useState(MENU[0].id);
  const { t, lang } = useLang();
  const category = MENU.find((c) => c.id === active);
  const featured = category.items.filter((i) => i.image);
  const rest = category.items.filter((i) => !i.image);
  const catLabel = (c) => (lang === "en" ? CAT_EN[c.id] || c.label : c.label);
  const extraTitle = (title) => (lang === "en" ? EXTRA_EN[title] || title : title);

  return (
    <main className="pt-20" data-testid="menu-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-12">
        <Reveal>
          <SectionLabel number="·">{t("menu.label")}</SectionLabel>
          <div className="flex items-end gap-4 sm:gap-6 flex-wrap">
            <img src="/assets/bella-script.png" alt="bella" className="h-16 sm:h-24 w-auto -mb-2" />
            <h1 className="font-display uppercase leading-[0.9] tracking-tight text-6xl sm:text-8xl text-foreground">Menù</h1>
          </div>
          <p className="text-fog text-lg mt-6 max-w-xl leading-relaxed">{t("menu.intro")}</p>
        </Reveal>
      </section>

      <div className="sticky top-20 z-40 bg-ink/95 backdrop-blur-xl border-y border-neon/15">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto" data-testid="menu-tabs">
          {MENU.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`menu-tab-${c.id}`}
              className={`font-head font-bold uppercase tracking-[0.15em] text-sm whitespace-nowrap px-5 py-4 border-b-2 transition-colors duration-300 ${
                active === c.id ? "text-neon border-neon" : "text-fog border-transparent hover:text-foreground"
              }`}
            >
              {catLabel(c)}
            </button>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {featured.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
                {featured.map((f) => (
                  <div key={f.name} className="relative border border-neon/25 overflow-hidden group rounded-sm" data-testid={`menu-featured-${f.name.toLowerCase()}`}>
                    <img src={f.image} alt={`Pizza ${f.name}`} loading="lazy" style={{ objectPosition: "center" }}
                      className="w-full aspect-[16/9] object-cover brightness-105 saturate-110 transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-baseline gap-3">
                        <h3 className="font-display uppercase text-3xl text-foreground">{f.name}</h3>
                        <span className="flex-1 border-b border-dotted border-foreground/30 -translate-y-1" />
                        <span className="font-display text-2xl text-neon">€{f.price}</span>
                      </div>
                      <p className="text-sm text-fog mt-2 max-w-md">{lang === "en" ? INGREDIENTS_EN[f.name] ?? f.ingredients : f.ingredients}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-12">
              <MenuSection title={category.firstTitle ? (lang === "en" ? category.firstTitle_en || category.firstTitle : category.firstTitle) : catLabel(category)} items={rest.length ? rest : category.items.filter((i) => !i.image)} lang={lang} />
              {category.extras?.map((x) => (
                <MenuSection key={x.title} title={extraTitle(x.title)} items={x.items} lang={lang} />
              ))}
            </div>

            {category.note && (
              <p className="text-fog/70 text-sm mt-10 tracking-wide">
                {lang === "en" ? "* bottle · ** glass only" : category.note}
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-20">
          <div className="border border-neon/25 bg-pine p-8 rounded-sm">
            <h3 className="font-head font-bold uppercase tracking-[0.25em] text-sm text-neon mb-6">{t("menu.extras")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {MENU_EXTRAS.map((e) => (
                <div key={e.name} className="flex items-baseline gap-3">
                  <span className="font-head font-semibold uppercase tracking-wide text-sm text-foreground">
                    {lang === "en" ? EXTRAS_EN[e.name] || e.name : e.name}
                  </span>
                  <span className="flex-1 border-b border-dotted border-foreground/25 -translate-y-1" />
                  <span className="font-display text-neon">€{e.price}</span>
                </div>
              ))}
            </div>
            <p className="text-fog/70 text-xs mt-6 leading-relaxed">{t("menu.allergens")}</p>
          </div>
        </Reveal>

        <Reveal className="mt-16 text-center">
          <p className="font-head uppercase tracking-[0.25em] text-fog text-sm mb-6">{t("menu.ctaQ")}</p>
          <Link to="/prenota" data-testid="menu-cta-prenota"
            className="inline-block bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-transform duration-300 hover:scale-105 hover:-rotate-1">
            {t("menu.book")}
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
